/**
 * T114-F5-h: Batch Delete Dialog
 * Destructive AlertDialog with preview step.
 */
import { useState } from 'react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Eye, Trash2 } from 'lucide-react';
import { useAppDispatch } from '@/features/hooks';
import { batchDeleteTransactions } from '@/features/transactions/transactionsSlice';
import { useToast } from '@/hooks/use-toast';
import { useCurrency } from '@/hooks/useCurrency';
import { useTimezone } from '@/hooks/useTimezone';
import type { BatchOperationResult } from '@/lib/types';

interface BatchDeleteDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  selectedIds: string[];
  onSuccess: () => void;
}

export function BatchDeleteDialog({
  open,
  onOpenChange,
  selectedIds,
  onSuccess,
}: BatchDeleteDialogProps) {
  const dispatch = useAppDispatch();
  const { toast } = useToast();
  const { fmt } = useCurrency();
  const { formatInUserTimezone } = useTimezone();

  const [preview, setPreview] = useState<BatchOperationResult | null>(null);
  const [isPreviewLoading, setIsPreviewLoading] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const handlePreview = async () => {
    setIsPreviewLoading(true);
    try {
      const result = await dispatch(
        batchDeleteTransactions({ transactionIds: selectedIds, dryRun: true })
      ).unwrap();
      setPreview(result);
    } catch (err) {
      toast({ title: 'Preview failed', description: String(err), variant: 'destructive' });
    } finally {
      setIsPreviewLoading(false);
    }
  };

  const handleDelete = async () => {
    setIsDeleting(true);
    try {
      const result = await dispatch(
        batchDeleteTransactions({ transactionIds: selectedIds, dryRun: false })
      ).unwrap();
      toast({
        title: 'Transactions deleted',
        description: `${result.affected} transaction${result.affected !== 1 ? 's' : ''} permanently deleted`,
      });
      onSuccess();
      handleClose();
    } catch (err) {
      toast({ title: 'Delete failed', description: String(err), variant: 'destructive' });
    } finally {
      setIsDeleting(false);
    }
  };

  const handleClose = () => {
    setPreview(null);
    onOpenChange(false);
  };

  return (
    <AlertDialog open={open} onOpenChange={handleClose}>
      <AlertDialogContent className="max-h-[85vh] overflow-y-auto" data-testid="batch-delete-dialog">
        <AlertDialogHeader>
          <AlertDialogTitle className="flex items-center gap-2 text-destructive">
            <Trash2 className="h-5 w-5" />
            Delete {selectedIds.length} Transaction{selectedIds.length !== 1 ? 's' : ''}?
          </AlertDialogTitle>
          <AlertDialogDescription>
            This action cannot be undone. All selected transactions will be permanently removed.
          </AlertDialogDescription>
        </AlertDialogHeader>

        {!preview ? (
          <div className="py-4">
            <p className="text-sm text-muted-foreground">
              Click &quot;Preview&quot; to see which transactions will be deleted.
            </p>
          </div>
        ) : (
          <div className="space-y-2 py-2 max-h-[300px] overflow-y-auto" data-testid="batch-delete-preview">
            {isPreviewLoading ? (
              <div className="space-y-2">
                {Array.from({ length: 3 }).map((_, i) => (
                  <Skeleton key={i} className="h-10 w-full" />
                ))}
              </div>
            ) : (
              preview.results?.slice(0, 50).map((r) => (
                <div
                  key={r.transactionId}
                  className="flex items-center justify-between p-2 rounded border text-sm"
                >
                  <div className="min-w-0 flex-1">
                    <p className="font-medium break-words">{r.description}</p>
                    <p className="text-xs text-muted-foreground">
                      {formatInUserTimezone(r.date, 'date-only')}
                    </p>
                  </div>
                  <span className="text-sm font-medium tabular-nums shrink-0 ml-3">
                    {fmt(r.amount)}
                  </span>
                </div>
              ))
            )}
          </div>
        )}

        <AlertDialogFooter className="gap-2">
          <AlertDialogCancel onClick={handleClose}>Cancel</AlertDialogCancel>
          {!preview ? (
            <Button onClick={handlePreview} disabled={isPreviewLoading} variant="outline">
              <Eye className="mr-2 h-4 w-4" />
              {isPreviewLoading ? 'Loading...' : 'Preview'}
            </Button>
          ) : (
            <>
              <Button variant="outline" onClick={() => setPreview(null)}>
                Back
              </Button>
              <AlertDialogAction
                onClick={handleDelete}
                disabled={isDeleting}
                data-testid="batch-delete-confirm-btn"
                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              >
                {isDeleting ? 'Deleting...' : `Delete ${preview.affected} transactions`}
              </AlertDialogAction>
            </>
          )}
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
